import { useSetAtom } from 'jotai'
import { type FC, type ReactNode, useEffect, useState } from 'react'
import {
  Dialog as AriaDialog,
  Modal as AriaModal,
  ModalOverlay as AriaModalOverlay,
} from 'react-aria-components'
import { useTranslation } from 'react-i18next'
import LucideArrowLeft from '~icons/lucide/arrow-left'
import Logo from 'src/components/Logo'
import { UserCancelled } from 'src/components/Modal/Confirm'
import { OrderSummary } from 'src/components/Payment/PaymentDialogV2/OrderSummary'
import { PaymentFormSection } from 'src/components/Payment/PaymentDialogV2/PaymentFormSection'
import { useCreditPackData } from 'src/components/Payment/PaymentDialogV2/useCreditPackData'
import { usePaymentProvider } from 'src/components/Payment/usePaymentProvider'
import { Interval } from 'src/services/apollo/generated/graphql'
import {
  getCurrentPrice,
  isPaymentDialogOpenAtom,
  type OpenPaymentDialogReturns,
  type PaymentOpts,
  type TrackingOpts,
} from 'src/services/helpers/payment'
import { cx } from 'src/utils/cx'

type PaymentDialogHandlers = {
  onSucceeded?: (_: OpenPaymentDialogReturns) => void
  onFailed?: (error: Error) => void
  onJcbAeUnionPayRequired?: () => void
}

type PaymentDialogV2Props = PaymentOpts & TrackingOpts & PaymentDialogHandlers

const PaymentDialogV2: FC<PaymentDialogV2Props> = ({
  onSucceeded,
  onFailed,
  onJcbAeUnionPayRequired,
  ...props
}) => {
  const [open, setOpen] = useState(true)
  useTrackDialogOpen()

  const { t } = useTranslation(['action'])
  const {
    loading,
    selectedPaymentProvider,
    setSelectedPaymentProvider,
    eligibleProviders,
  } = usePaymentProvider(props)

  const currentPrice = getCurrentPrice({
    selectedPaymentProvider,
    prices: props.prices,
    currency: props.currency,
  })

  const tierLabel = useTierLabel(props.oneTimeMembership?.tier)
  const { packageContents, packCardMeta, baseCredits } =
    useCreditPackData(props)

  const cancel = () => {
    setOpen(false)
    setTimeout(() => onFailed?.(UserCancelled), 150)
  }

  const handleJcbAeUnionPayRequired = () => {
    setOpen(false)
    setTimeout(() => onJcbAeUnionPayRequired?.(), 150)
  }

  return (
    <FullScreenDialog open={open} onClose={cancel}>
      <header className="flex items-center px-4 sm:px-8 py-3 sm:py-4 border-b border-card-border bg-modal-background sticky top-0 z-10">
        <Logo className="h-6 w-auto" />
      </header>

      <div className="flex-1 w-full max-w-4xl mx-auto px-4 sm:px-8 py-6 sm:py-12">
        <button
          onClick={cancel}
          className={cx(
            'flex items-center gap-1.5',
            '-ml-2 px-2 py-1.5 mb-4',
            'rounded-full hover:bg-hover transition-colors',
            'text-sm font-medium text-primary/70 hover:text-primary',
          )}
        >
          <LucideArrowLeft className="size-4" />
          <span>{t('action:back', { defaultValue: 'Back' })}</span>
        </button>
        <div className="grid grid-cols-1 md:grid-cols-[3fr_2fr] gap-6 md:gap-12">
          <section className="order-2 md:order-1 min-w-0">
            <PaymentFormSection
              loading={loading}
              eligibleProviders={eligibleProviders}
              selectedPaymentProvider={selectedPaymentProvider}
              setSelectedPaymentProvider={setSelectedPaymentProvider}
              paymentOpts={props}
              onSucceeded={onSucceeded}
              onFailed={onFailed}
              onJcbAeUnionPayRequired={handleJcbAeUnionPayRequired}
            />
          </section>

          <aside className="text-primary order-1 md:order-2 min-w-0">
            <OrderSummary
              tierLabel={tierLabel}
              isMembership={!!props.oneTimeMembership}
              isAnnual={props.interval === Interval.Year}
              credits={props.credits}
              baseCredits={baseCredits}
              currentPrice={currentPrice}
              packageContents={packageContents}
              packCardMeta={packCardMeta}
            />
          </aside>
        </div>
      </div>
    </FullScreenDialog>
  )
}

const FullScreenDialog: FC<{
  open: boolean
  onClose: () => void
  children: ReactNode
}> = ({ open, onClose, children }) => (
  <AriaModalOverlay
    isOpen={open}
    onOpenChange={isOpen => {
      if (!isOpen) onClose()
    }}
    isKeyboardDismissDisabled
    className={cx(
      'fixed inset-0 z-[1400] bg-background',
      '!duration-150',
      'entering:animate-in entering:fade-in',
      'exiting:animate-out exiting:fade-out',
    )}
  >
    <AriaModal
      className={cx(
        'fixed inset-0 z-[1400] overflow-y-auto',
        'entering:animate-in entering:fade-in entering:duration-200',
        'exiting:animate-out exiting:fade-out exiting:duration-150',
      )}
    >
      <AriaDialog className="outline-none min-h-full flex flex-col">
        {children}
      </AriaDialog>
    </AriaModal>
  </AriaModalOverlay>
)

function useTrackDialogOpen() {
  const setIsPaymentDialogOpen = useSetAtom(isPaymentDialogOpenAtom)
  useEffect(() => {
    setIsPaymentDialogOpen(true)
    return () => setIsPaymentDialogOpen(false)
  }, [setIsPaymentDialogOpen])
}

function useTierLabel(tier: number | undefined) {
  const { t } = useTranslation(['membership'])
  if (tier == null) return ''
  return t(`membership:membership.tier.${tier}.label`, { defaultValue: '' })
}

export default PaymentDialogV2
